"use client";

//import { memo } from "react";
import { PropsWithChildren, useEffect } from "react";
import { KEYS } from "@/src/const";
import CMSLogic from "@/src/logic/CMSLogic";
import { StrapiCollectionType, StrapiSingleType } from "@/src/type";
import { Log, LocalizationHelper } from "@/src/util";
import { CMSContext } from "./CMSContext";

interface WithCMSProps {
  locale: string;
  webConfig?: StrapiSingleType.WebConfig;
  dontLoadConfig?: boolean;
  defaultQuery?: string;
  propertySlug?: string;
  rootPrefix?: string;
  galleries?: StrapiCollectionType.Gallery[];
}

function CMSProvider({ children, locale, webConfig, defaultQuery, propertySlug, rootPrefix, galleries }: PropsWithChildren<WithCMSProps>) {
  /**
   * Show loaded web config
   */
  useEffect(() => {
    if (locale) {
      LocalizationHelper.setLocaleToLocalStorage(locale);
    }

    if (!webConfig) {
      return;
    }

    Log.logToConsoleDebug(
      "CMSProvider.tsx",
      "Web Config Loaded",
      [webConfig, galleries, { locale, dir: LocalizationHelper.localeDirection(locale) }],
      Log.LOG_COLORS.STATE_CHANGE.BG_COLOR,
      Log.LOG_COLORS.STATE_CHANGE.COLOR
    );
  }, [webConfig, locale, galleries]);

  /**
   * A function to construct a relative root using the `c` prefix and locale code
   * @param path - the page path without the `/c/${locale}`
   * @returns
   */
  function constructLink(path: string, overrideLocal?: string | null) {
    const queryParams = typeof window !== "undefined" ? window.location.search : defaultQuery;
    const searchParams = new URLSearchParams(defaultQuery);
    const profile = searchParams?.get(KEYS.SEARCH_PARAM.BROKER_PROFILE);
    return CMSLogic.constructLink(path, overrideLocal ?? locale, profile, queryParams, propertySlug, rootPrefix);
  }

  /**
   * A function to change the current language / locale of the user
   * @param destinationLocale - the destination locale
   * @returns
   */
  function changeLocale(destinationLocale: string) {
    return CMSLogic.changeLocale(locale, destinationLocale, rootPrefix);
  }

  return (
    <CMSContext.Provider
      value={{
        webConfig,
        locale,
        direction: LocalizationHelper.localeDirection(locale),
        propertySlug,
        galleries,
        constructLink,
        changeLocale,
      }}
    >
      {children}
    </CMSContext.Provider>
  );
}

export default CMSProvider;
//const MemorizedProvider = memo(CMSProvider);
//export default MemorizedProvider;
